import {View} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import type React from 'react'

import {useKawaiiMode} from '#/state/preferences/kawaii'
import {useSession} from '#/state/session'
import {useShellLayout} from '#/state/shell/shell-layout'
import {HomeHeaderLayoutMobile} from '#/view/com/home/HomeHeaderLayoutMobile'
import {Logo} from '#/view/icons/Logo'
import {atoms as a, select, useBreakpoints, useGutters, useTheme, web} from '#/alf'
import {ButtonIcon} from '#/components/Button'
import {Hashtag_Stroke2_Corner0_Rounded as FeedsIcon} from '#/components/icons/Hashtag'
import * as Layout from '#/components/Layout'
import {Link} from '#/components/Link'

type HomeHeaderLayoutProps = {
  children: React.ReactNode
  tabBarAnchor: JSX.Element | null | undefined
  variant?: 'default' | 'gallery'
}

export function HomeHeaderLayout(props: HomeHeaderLayoutProps) {
  const {gtMobile} = useBreakpoints()
  if (props.variant === 'gallery' || !gtMobile) {
    return <HomeHeaderLayoutMobile {...props} />
  } else {
    const {variant: _variant, title: _title, ...rest} = props
    return <HomeHeaderLayoutDesktopAndTablet {...rest} />
  }
}

function HomeHeaderLayoutDesktopAndTablet({
  children,
  tabBarAnchor,
}: {
  children: React.ReactNode
  tabBarAnchor: JSX.Element | null | undefined
}) {
  const t = useTheme()
  const {headerHeight} = useShellLayout()
  const {hasSession} = useSession()
  const {_} = useLingui()
  const kawaii = useKawaiiMode()
  const gutters = useGutters([0, 'base'])
  const glassBackground = select(t.name, {
    light: 'rgba(255,255,255,0.72)',
    dark: 'rgba(15,23,42,0.65)',
    dim: 'rgba(15,23,42,0.65)',
  })
  const glassBorder = select(t.name, {
    light: 'rgba(15,23,42,0.08)',
    dark: 'rgba(255,255,255,0.08)',
    dim: 'rgba(255,255,255,0.08)',
  })
  const glassShadow = select(t.name, {
    light: '0 8px 24px rgba(15,23,42,0.06)',
    dark: '0 8px 20px rgba(0,0,0,0.25)',
    dim: '0 8px 20px rgba(0,0,0,0.25)',
  })

  return (
    <>
      {hasSession && (
        <Layout.Center>
          <View
            style={[a.flex_row, a.align_center, gutters, a.pt_md, t.atoms.bg]}>
            <View style={{width: 34}} />
            <View style={[a.flex_1, a.align_center, a.justify_center]}>
              <Logo width={kawaii ? 60 : 28} />
            </View>
            <Link
              to="/feeds"
              hitSlop={10}
              label={_(msg`View your feeds and explore more`)}
              size="small"
              variant="ghost"
              color="secondary"
              shape="square"
              style={[a.justify_center]}>
              <ButtonIcon icon={FeedsIcon} size="lg" />
            </Link>
          </View>
        </Layout.Center>
      )}
      {tabBarAnchor}
      <Layout.Center
        style={[
          a.sticky,
          a.z_10,
          a.align_center,
          {
            backgroundColor: glassBackground,
            borderBottomWidth: 1,
            borderBottomColor: glassBorder,
          },
          web({
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            boxShadow: glassShadow,
          }),
          {top: 0},
        ]}
        onLayout={e => {
          headerHeight.set(e.nativeEvent.layout.height)
        }}>
        {children}
      </Layout.Center>
    </>
  )
}
